/* project-specs.jsx - Specs phase tab for software projects.
   Keeps generated specifications separate from Design inputs and Board tickets.
   Exports: window.ProjectSpecsTab */
(function () {
  const { useEffect, useMemo, useState } = React;

  const { fmtDateTime } = window.AC;

  const arrayFrom = (value, keys = []) => {
    if (Array.isArray(value)) return value;
    for (const key of keys) {
      if (Array.isArray(value && value[key])) return value[key];
    }
    return [];
  };

  const textOf = (value, fallback = '') => {
    if (value == null) return fallback;
    if (typeof value === 'string') return value;
    return value.title || value.name || value.question || value.text || value.body || value.description || value.summary || fallback;
  };

  const detailOf = (value) => {
    if (value == null || typeof value === 'string') return '';
    return value.detail || value.details || value.body || value.answer || value.notes || value.description || value.text || '';
  };

  const firstText = (value, keys = []) => {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    for (const key of keys) {
      const text = value && value[key];
      if (String(text || '').trim()) return String(text);
    }
    return '';
  };

  const statusText = (value, fallback = 'Draft') => String(value || fallback).trim() || fallback;

  const statusClass = (value) => {
    const clean = String(value || '').toLowerCase();
    if (clean.includes('approved') || clean.includes('ready') || clean.includes('current') || clean.includes('ok')) return 'Active';
    if (clean.includes('question') || clean.includes('stale') || clean.includes('pending')) return 'Partial';
    if (clean.includes('error') || clean.includes('failed') || clean.includes('rejected')) return 'Overdue';
    return 'Draft';
  };

  const getSpecId = (spec) => spec && (spec.id || spec.specId || spec.key);

  const normalizeSpecList = (payload) => arrayFrom(payload, ['specs', 'items', 'results'])
    .map((spec, index) => ({
      ...spec,
      id: getSpecId(spec) || String(index),
      title: spec.title || spec.name || `Spec ${index + 1}`,
      summary: spec.summary || spec.description || spec.overview || spec.intent || spec.scope || '',
      status: spec.status || spec.state || '',
      approvalStatus: spec.approvalStatus || spec.approval || (spec.approvedAt ? 'Approved' : ''),
      approvedAt: spec.approvedAt || spec.approvalAt || '',
      updatedAt: spec.updatedAt || spec.modifiedAt || spec.createdAt || '',
    }));

  const normalizeSpecDetail = (spec) => {
    const sourceSnapshot = spec.sourceSnapshot || spec.snapshot || {};
    return {
      ...spec,
      id: getSpecId(spec),
      title: spec.title || spec.name || 'Untitled spec',
      summary: spec.summary || spec.description || spec.overview || spec.intent || spec.scope || '',
      status: spec.status || spec.state || '',
      approvalStatus: spec.approvalStatus || spec.approval || (spec.approvedAt ? 'Approved' : ''),
      approvedAt: spec.approvedAt || spec.approvalAt || '',
      updatedAt: spec.updatedAt || spec.modifiedAt || spec.createdAt || '',
      sourceSnapshotStatus: spec.sourceSnapshotStatus || spec.snapshotStatus || sourceSnapshot.status || (spec.sourceSnapshotHash ? 'Captured' : ''),
      sourceSnapshotUpdatedAt: spec.sourceSnapshotUpdatedAt || sourceSnapshot.updatedAt || sourceSnapshot.createdAt || '',
      requirements: arrayFrom(spec.requirements || spec.requirementItems, ['requirements', 'items']),
      acceptanceChecks: arrayFrom(spec.acceptanceChecks || spec.acceptanceCriteria || spec.checks, ['acceptanceChecks', 'checks', 'items']),
      openQuestions: arrayFrom(spec.openQuestions || spec.questions, ['openQuestions', 'questions', 'items']),
      detailFields: [
        ['Intent', spec.intent],
        ['Scope', spec.scope],
        ['Non-goals', spec.nonGoals],
        ['Affected product areas', spec.affectedProductAreas],
        ['User workflow', spec.userWorkflow],
        ['Data concepts', spec.dataConcepts],
        ['Permissions', spec.permissions],
        ['Error cases', spec.errorCases],
        ['Rollout notes', spec.rolloutNotes],
        ['Ticket planning guidance', spec.ticketPlanningGuidance],
      ].filter((item) => String(item[1] || '').trim()),
    };
  };

  function downloadMarkdownFile(filename, text) {
    const value = String(text || '');
    if (!value.trim()) return false;
    const blob = new Blob([value], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    return true;
  }

  const md = (value) => String(value || '').trim();

  function appendMarkdownSection(lines, title, value) {
    const text = md(value);
    if (!text) return;
    lines.push('', `## ${title}`, '', text);
  }

  function appendMarkdownList(lines, title, items, formatter) {
    const list = Array.isArray(items) ? items : [];
    if (!list.length) return;
    lines.push('', `## ${title}`, '');
    list.forEach((item, index) => {
      const text = formatter(item, index);
      if (text) lines.push(text);
    });
  }

  function formatRequirement(item, index) {
    const key = firstText(item, ['key']);
    const title = textOf(item, `Requirement ${index + 1}`);
    const body = detailOf(item);
    const priority = firstText(item, ['priority']);
    const status = firstText(item, ['status']);
    const meta = [key, priority && `Priority: ${priority}`, status && `Status: ${status}`].filter(Boolean).join(' | ');
    return `- ${meta ? `**${meta}** - ` : ''}${title}${body && body !== title ? `\n  ${body}` : ''}`;
  }

  function formatAcceptanceCheck(item, index) {
    const key = firstText(item, ['key']);
    const title = textOf(item, `Acceptance check ${index + 1}`);
    const body = detailOf(item);
    const checkType = firstText(item, ['checkType', 'type']);
    const requirement = firstText(item, ['requirementKey']);
    const meta = [key, checkType, requirement && `Requirement: ${requirement}`].filter(Boolean).join(' | ');
    return `- ${meta ? `**${meta}** - ` : ''}${title}${body && body !== title ? `\n  ${body}` : ''}`;
  }

  function formatQuestion(item, index) {
    const key = firstText(item, ['key']);
    const question = firstText(item, ['question', 'title', 'text']) || `Question ${index + 1}`;
    const answer = firstText(item, ['answer', 'body']);
    const status = firstText(item, ['status']);
    const meta = [key, status && `Status: ${status}`].filter(Boolean).join(' | ');
    return `- ${meta ? `**${meta}** - ` : ''}${question}${answer ? `\n  Answer: ${answer}` : ''}`;
  }

  function formatSource(item, index) {
    const key = firstText(item, ['key']);
    const title = firstText(item, ['title']) || `Source ${index + 1}`;
    const type = firstText(item, ['sourceType', 'type']);
    const citation = firstText(item, ['citation']);
    const meta = [key, type].filter(Boolean).join(' | ');
    return `- ${meta ? `**${meta}** - ` : ''}${title}${citation ? `\n  ${citation}` : ''}`;
  }

  function buildSpecAgentMarkdown(project, specs) {
    const selected = (Array.isArray(specs) ? specs : []).filter(Boolean);
    const projectName = md(project && project.name) || 'Project';
    const lines = [
      `# Implementation spec export: ${projectName}`,
      '',
      'Use the selected approved specs below as the implementation contract. Preserve requirement keys in commits, tickets, tests, and review notes where practical.',
    ];
    selected.forEach((spec, index) => {
      lines.push('', '---', '', `# Spec ${index + 1}: ${md(spec.title) || 'Untitled spec'}`);
      const meta = [
        firstText(spec, ['key']) && `Key: ${firstText(spec, ['key'])}`,
        spec.version != null && `Version: ${spec.version}`,
        firstText(spec, ['status']) && `Status: ${firstText(spec, ['status'])}`,
        firstText(spec, ['approvedAt']) && `Approved: ${firstText(spec, ['approvedAt'])}`,
        firstText(spec, ['sourceSnapshotHash']) && `Source snapshot: ${firstText(spec, ['sourceSnapshotHash'])}`,
      ].filter(Boolean);
      if (meta.length) lines.push('', meta.join('\n'));
      appendMarkdownSection(lines, 'Intent', spec.intent || spec.summary);
      appendMarkdownSection(lines, 'Scope', spec.scope);
      appendMarkdownSection(lines, 'Non-goals', spec.nonGoals);
      appendMarkdownSection(lines, 'Affected product areas', spec.affectedProductAreas);
      appendMarkdownSection(lines, 'User workflow', spec.userWorkflow);
      appendMarkdownSection(lines, 'Data concepts', spec.dataConcepts);
      appendMarkdownSection(lines, 'Permissions', spec.permissions);
      appendMarkdownSection(lines, 'Error cases', spec.errorCases);
      appendMarkdownSection(lines, 'Rollout notes', spec.rolloutNotes);
      appendMarkdownSection(lines, 'Ticket planning guidance', spec.ticketPlanningGuidance);
      appendMarkdownList(lines, 'Requirements', spec.requirements, formatRequirement);
      appendMarkdownList(lines, 'Acceptance checks', spec.acceptanceChecks, formatAcceptanceCheck);
      appendMarkdownList(lines, 'Open questions', spec.openQuestions || spec.questions, formatQuestion);
      appendMarkdownList(lines, 'Sources', spec.sources, formatSource);
    });
    return lines.join('\n').replace(/\n{3,}/g, '\n\n').trim() + '\n';
  }

  function downloadFilename(project, specs) {
    const selected = (Array.isArray(specs) ? specs : []).filter(Boolean);
    const base = selected.length === 1
      ? (selected[0].key || selected[0].title || 'spec')
      : `${(project && project.name) || 'project'}-specs`;
    return `${String(base).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'spec'}-implementation.md`;
  }

  function SpecEmpty({ title, children }) {
    return (
      <div className="spec-empty">
        <Icon name="doc" size={18} />
        <strong>{title}</strong>
        {children && <span>{children}</span>}
      </div>
    );
  }

  function SpecList({ specs, selectedId, exportIds, onSelect, onToggleExport }) {
    if (!specs.length) return <SpecEmpty title="No specs yet">Generate the first spec from the current project inputs.</SpecEmpty>;
    return (
      <div className="spec-list">
        {specs.map((spec) => (
          <button key={spec.id} type="button" className={'spec-row' + (selectedId === spec.id ? ' on' : '')} onClick={() => onSelect(spec.id)}>
            <input
              type="checkbox"
              checked={exportIds.includes(spec.id)}
              aria-label={`Select ${spec.title} for export`}
              onChange={(event) => onToggleExport(spec.id, event.target.checked)}
              onClick={(event) => event.stopPropagation()}
            />
            <span>
              <strong>{spec.title}</strong>
              {spec.summary && <small>{spec.summary}</small>}
            </span>
            <Pill kind={statusClass(spec.approvalStatus || spec.status)} sm>{statusText(spec.approvalStatus || spec.status)}</Pill>
          </button>
        ))}
      </div>
    );
  }

  function SpecItemList({ title, items, empty, icon }) {
    return (
      <section className="spec-section">
        <div className="spec-section-head"><Icon name={icon || 'list'} size={14} /><h3>{title}</h3><span>{items.length}</span></div>
        {items.length ? (
          <div className="spec-items">
            {items.map((item, index) => {
              const detail = detailOf(item);
              return (
                <div key={(item && item.id) || index} className="spec-item">
                  <div className="spec-item-index">{index + 1}</div>
                  <div>
                    <strong>{textOf(item, `Item ${index + 1}`)}</strong>
                    {detail && detail !== textOf(item) && <p>{detail}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        ) : <div className="spec-muted">{empty}</div>}
      </section>
    );
  }

  function SpecNarrative({ fields }) {
    if (!fields || !fields.length) return null;
    return (
      <section className="spec-section">
        <div className="spec-section-head"><Icon name="doc" size={14} /><h3>Spec detail</h3><span>{fields.length}</span></div>
        <div className="spec-fields">
          {fields.map(([label, value]) => (
            <div className="spec-field" key={label}>
              <span>{label}</span>
              <p>{value}</p>
            </div>
          ))}
        </div>
      </section>
    );
  }

  function SpecDetail({ project, spec, busy, agentPackNotice, onGenerate, onSave, onDelete, onApprove, onRefreshSnapshot, onCreateTicketPlan, onExport }) {
    if (!spec) {
      return (
        <Panel title="Spec detail" sub="Generate a spec to start the Specs phase">
          <SpecEmpty title="No spec selected">Specs stay focused on requirements, checks, questions, snapshots, and approval.</SpecEmpty>
          <div className="spec-actions-line">
            <button className="btn sm" type="button" disabled={busy} onClick={onGenerate}><Icon name="plus" size={13} />Generate spec</button>
          </div>
        </Panel>
      );
    }

    const approved = String(spec.approvalStatus || '').toLowerCase().includes('approved') || !!spec.approvedAt;
    return (
      <Panel
        title={spec.title}
        sub={spec.updatedAt ? `Updated ${fmtDateTime ? fmtDateTime(spec.updatedAt) : spec.updatedAt}` : project.name}
        actions={(
          <>
            <button className="btn ghost sm" type="button" disabled={busy} onClick={onGenerate}><Icon name="plus" size={13} />Generate</button>
            <button className="btn ghost sm" type="button" disabled={busy} onClick={onSave}><Icon name="refresh" size={13} />Update</button>
            <button className="btn ghost sm" type="button" disabled={busy} onClick={onRefreshSnapshot}><Icon name="archive" size={13} />Snapshot</button>
            <button className="btn ghost sm pos" type="button" disabled={busy || approved} onClick={onApprove}><Icon name="check" size={13} />Approve</button>
            <button className="btn ghost sm" type="button" disabled={busy || !approved} onClick={onCreateTicketPlan}><Icon name="route" size={13} />Create ticket plan</button>
            <button className="btn ghost sm" type="button" disabled={busy} onClick={onExport}><Icon name="download" size={13} />Download MD</button>
            <button className="btn danger sm" type="button" disabled={busy} onClick={onDelete}><Icon name="trash" size={13} />Delete</button>
          </>
        )}
      >
        <div className="spec-detail">
          <div className="spec-status-grid">
            <div><span>Status</span><Pill kind={statusClass(spec.status)}>{statusText(spec.status)}</Pill></div>
            <div><span>Approval</span><Pill kind={statusClass(spec.approvalStatus || (approved ? 'Approved' : 'Draft'))}>{statusText(spec.approvalStatus || (approved ? 'Approved' : 'Draft'))}</Pill></div>
            <div><span>Source snapshot</span><Pill kind={statusClass(spec.sourceSnapshotStatus)}>{statusText(spec.sourceSnapshotStatus, 'Not captured')}</Pill></div>
            <div><span>Snapshot updated</span><strong>{spec.sourceSnapshotUpdatedAt ? (fmtDateTime ? fmtDateTime(spec.sourceSnapshotUpdatedAt) : spec.sourceSnapshotUpdatedAt) : '-'}</strong></div>
          </div>

          {spec.summary && <p className="spec-summary">{spec.summary}</p>}
          {agentPackNotice && <div className="spec-agent-pack"><Icon name="check" size={14} />{agentPackNotice}</div>}

          <SpecNarrative fields={spec.detailFields} />
          <SpecItemList title="Requirements" icon="flag" items={spec.requirements} empty="No spec requirements returned yet." />
          <SpecItemList title="Acceptance checks" icon="check" items={spec.acceptanceChecks} empty="No acceptance checks returned yet." />
          <SpecItemList title="Open questions" icon="alert" items={spec.openQuestions} empty="No open questions." />
        </div>
      </Panel>
    );
  }

  function ProjectSpecsTab({ project, onAction, onToast, onError }) {
    const [specs, setSpecs] = useState([]);
    const [selectedId, setSelectedId] = useState('');
    const [detail, setDetail] = useState(null);
    const [exportIds, setExportIds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [agentPackNotice, setAgentPackNotice] = useState('');

    const selectedListSpec = useMemo(() => specs.find((spec) => spec.id === selectedId) || null, [specs, selectedId]);

    const loadSpecs = React.useCallback(async (preferredId) => {
      try {
        setLoading(true);
        const next = normalizeSpecList(await window.API.listProjectSpecs(project.id));
        setSpecs(next);
        const nextId = preferredId || selectedId || (next[0] && next[0].id) || '';
        setSelectedId(next.some((spec) => spec.id === nextId) ? nextId : ((next[0] && next[0].id) || ''));
        setExportIds((ids) => ids.filter((id) => next.some((spec) => spec.id === id)));
        setError('');
      } catch (e) {
        const message = (e && e.message) || 'Failed to load specs.';
        setError(message);
        if (onError) onError(message);
      } finally {
        setLoading(false);
      }
    }, [project.id, selectedId, onError]);

    useEffect(() => { loadSpecs(''); }, [project.id]);

    useEffect(() => {
      let cancelled = false;
      async function loadDetail() {
        if (!selectedId) {
          setDetail(null);
          return;
        }
        try {
          const next = normalizeSpecDetail(await window.API.getProjectSpec(project.id, selectedId));
          if (!cancelled) {
            setDetail(next);
            setAgentPackNotice('');
            setError('');
          }
        } catch (e) {
          if (!cancelled) {
            setDetail(selectedListSpec ? normalizeSpecDetail(selectedListSpec) : null);
            setError((e && e.message) || 'Failed to load spec detail.');
          }
        }
      }
      loadDetail();
      return () => { cancelled = true; };
    }, [project.id, selectedId, selectedListSpec]);

    function toggleExportSpec(specId, checked) {
      setExportIds((ids) => {
        const next = ids.filter((id) => id !== specId);
        return checked ? [...next, specId] : next;
      });
    }

    async function runAction(fn) {
      try {
        setBusy(true);
        setError('');
        return await fn();
      } catch (e) {
        const message = (e && e.message) || 'Spec action failed.';
        setError(message);
        if (onError) onError(message);
        return null;
      } finally {
        setBusy(false);
      }
    }

    const generateSpec = () => runAction(async () => {
      const created = await onAction('createProjectSpec', { projectId: project.id, spec: { title: `${project.name || 'Project'} spec`, sourceSpecId: selectedId || null } });
      await loadSpecs(getSpecId(created) || selectedId);
    });

    const saveSpec = () => detail && runAction(async () => {
      const saved = await onAction('updateProjectSpec', { projectId: project.id, specId: detail.id, spec: detail });
      await loadSpecs(getSpecId(saved) || detail.id);
    });

    const deleteSpec = () => detail && runAction(async () => {
      const ok = await onAction('deleteProjectSpec', { projectId: project.id, specId: detail.id, title: detail.title });
      if (ok !== false) {
        setDetail(null);
        await loadSpecs('');
      }
    });

    const approveSpec = () => detail && runAction(async () => {
      const saved = await onAction('approveProjectSpec', { projectId: project.id, specId: detail.id });
      await loadSpecs(getSpecId(saved) || detail.id);
    });

    const refreshSnapshot = () => detail && runAction(async () => {
      const saved = await onAction('refreshProjectSpecSnapshot', { projectId: project.id, specId: detail.id });
      await loadSpecs(getSpecId(saved) || detail.id);
    });

    const createTicketPlan = () => detail && runAction(async () => {
      const pack = await onAction('createProjectSpecTicketPlan', { projectId: project.id, specId: detail.id });
      const requirementCount = arrayFrom(pack && pack.requirements, ['requirements', 'items']).length || detail.requirements.length;
      const checkCount = arrayFrom(pack && pack.acceptanceChecks, ['acceptanceChecks', 'checks', 'items']).length || detail.acceptanceChecks.length;
      setAgentPackNotice(`Ticket plan source ready: ${requirementCount} requirements and ${checkCount} acceptance checks.`);
      if (onToast) onToast('Ticket plan source ready');
    });

    const exportSelectedSpecs = () => runAction(async () => {
      const ids = exportIds.length ? exportIds : (selectedId ? [selectedId] : []);
      if (!ids.length) return null;
      const loaded = await Promise.all(ids.map(async (id) => {
        try {
          return normalizeSpecDetail(await window.API.getProjectSpecAgentPack(project.id, id));
        } catch {
          return normalizeSpecDetail(await window.API.getProjectSpec(project.id, id));
        }
      }));
      const downloaded = downloadMarkdownFile(downloadFilename(project, loaded), buildSpecAgentMarkdown(project, loaded));
      if (!downloaded) throw new Error('Spec Markdown download failed.');
      const label = loaded.length === 1 ? 'Spec Markdown downloaded' : `${loaded.length} specs downloaded as Markdown`;
      setAgentPackNotice(label);
      if (onToast) onToast(label);
      return loaded;
    });

    return (
      <div className="spec-shell">
        <Panel
          title="Specs"
          sub="Generated software specifications"
          actions={(
            <>
              <button className="btn ghost sm" type="button" disabled={busy || (!selectedId && !exportIds.length)} onClick={exportSelectedSpecs}><Icon name="download" size={13} />Download selected</button>
              <button className="btn sm" type="button" disabled={busy} onClick={generateSpec}><Icon name="plus" size={13} />Generate spec</button>
            </>
          )}
        >
          {loading ? <div className="spec-muted">Loading specs...</div> : <SpecList specs={specs} selectedId={selectedId} exportIds={exportIds} onSelect={setSelectedId} onToggleExport={toggleExportSpec} />}
          {error && <div className="spec-error"><Icon name="alert" size={14} />{error}</div>}
        </Panel>
        <SpecDetail
          project={project}
          spec={detail}
          busy={busy}
          agentPackNotice={agentPackNotice}
          onGenerate={generateSpec}
          onSave={saveSpec}
          onDelete={deleteSpec}
          onApprove={approveSpec}
          onRefreshSnapshot={refreshSnapshot}
          onCreateTicketPlan={createTicketPlan}
          onExport={exportSelectedSpecs}
        />
      </div>
    );
  }

  Object.assign(window, { ProjectSpecsTab });
})();
